<template>
  <v-row no-gutters class="flex-column pa-1">
    <!-- header area-->
    <v-card v-if="!hideHeader" class="d-flex align-center justify-space-around" elevation="2" height="60px" style="border-radius: 4px 4px 0px 0px">
      <slot name="header">
        <!-- back button -->
        <v-col cols="auto" v-if="$listeners.onButtonBackClick">
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-on="on"
                v-bind="attrs"
                @click="handleBackButtonClick"
                :disabled="disableBackButton"
                variant="outlined"
                size="large"
                icon
                color="info">
                <v-icon>mdi-keyboard-backspace</v-icon>
              </v-btn>
            </template>
            <span> {{ headerBackTooltip }} </span>
          </v-tooltip>
        </v-col>
        <!-- header content -->
        <v-col>
          <v-list-item two-line>
            <!-- preview image -->
            <v-list-item-avatar v-if="!isNoWs(headerImage)">
              <v-img :src="base64ImgPrefix + headerImage" />
            </v-list-item-avatar>

            <v-list-item-content>
              <!-- title -->
              <v-list-item-title class="text-truncate"> {{ headerTitle }}</v-list-item-title>
              <!-- designation -->
              <v-list-item-subtitle v-if="!isNoWs(headerSubTitle)"> {{ headerSubTitle }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-col>
      </slot>
    </v-card>

    <!-- details area -->
    <v-card tile id="details-area" style="overflow: auto">
      <slot name="details" />
    </v-card>

    <!-- action button area -->
    <v-card
      v-if="!hideActions"
      class="d-flex align-center justify-space-around"
      elevation="2"
      id="wrapper-actions"
      :height="showTwoActionLines ? '120px' : '60px'"
      style="border-radius: 0px 0px 4px 4px; z-index: 10">
      <slot name="actionbuttons" />
    </v-card>
  </v-row>
</template>

<script>
// mixins
import HelpersMixin from '@/mixins/helpers.js';
import Responsive from '@/mixins/responsive.js';

export default {
  name: 'ApplicationWrapper',

  emits: ['onButtonBackClick', 'update:actionArea'],

  mixins: [HelpersMixin, Responsive],

  props: {
    headerImage: String,
    headerTitle: String,
    headerSubTitle: String,
    headerBackTooltip: String,
    disableBackButton: Boolean,
    hideHeader: Boolean,
    hideActions: Boolean,
    showTwoActionLines: Boolean,
  },

  data() {
    return {};
  },

  async mounted() {
    await this.setMaxHeightForDetails();

    var navElem = document.getElementById('layout-navigation');
    if (navElem) this.initNavAreaObserver(navElem);

    this.$emit('update:actionArea', await this.shouldWrapActionsInTwoLines());
  },

  //disconnect the observer before destroy
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
  },

  methods: {
    handleBackButtonClick() {
      this.$emit('onButtonBackClick');
    },
    async setMaxHeightForDetails() {
      await this.$nextTick();

      var detailsElem = document.getElementById('details-area');
      if (!detailsElem) return;

      // the sorting is not displayed when the application wrapper is active
      var navHeight = await this.navigationHeight();

      // a timeout is needed here, because the element has not yet been completely loaded at mount()
      setTimeout(() => {
        // the header has a fixed height of 60px
        var headerHeight = this.hideHeader ? '+ 60' : '0';

        var actionAreaHeight = this.showTwoActionLines ? ' - 60px' : '';

        var cssString =
          this.currentBreakpoint === 4
            ? 'calc(100vh - 41px - 111px)'
            : `calc(100vh - 41px - 111px - ${navHeight}px + ${headerHeight}px` + actionAreaHeight;

        detailsElem.style.maxHeight = cssString;
      }, 0);
    },
  },
  computed: {
    breakpoint() {
      return this.$vuetify.breakpoint.name;
    },
    navHeight() {
      return this.observedNavigationHeight;
    },
  },

  watch: {
    async observedNavigationHeight() {
      await this.setMaxHeightForDetails();
    },
    async breakpoint() {
      // if the breakpoint changes check if the action area should wrap in two lines
      this.$emit('update:actionArea', await this.shouldWrapActionsInTwoLines());
      await this.setMaxHeightForDetails();
    },

    async showTwoActionLines() {
      await this.setMaxHeightForDetails();
    },
  },
};
</script>

<style></style>
